import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';
import ProceedAtRiskSetting from './ProceedAtRiskSetting'; // #3188 APPROVE.5

const PLATFORM_DEFAULTS = {
  stallNotifyDays: 5, stallUrgentDays: 7, stallPartnerDays: 14,
  approvalNotifyDays: 3, approvalUrgentDays: 5, approvalRenewalDays: 7, approvalEscalateDays: 10,
} as const;

type Field = keyof typeof PLATFORM_DEFAULTS;

interface RuleRow {
  ruleType: 'CLIENT_APPROVAL_OVERDUE' | 'PHASE_STALL';
  source: 'project' | 'tenant';
  stallNotifyDays: number | null;
  stallUrgentDays: number | null;
  stallPartnerDays: number | null;
  approvalNotifyDays: number | null;
  approvalUrgentDays: number | null;
  approvalRenewalDays: number | null;
  approvalEscalateDays: number | null;
  /** #3188 — present (CLIENT_APPROVAL_OVERDUE only) once the API sends it, after 3b's switch-on. */
  allowProceedAtRisk?: boolean | null;
}

type RulesResponse = { rules: RuleRow[]; effective?: { allowProceedAtRisk?: boolean } };

interface Props {
  projectId: string;
}

const FIELD_BY_RULE: Record<string, Array<{ field: Field; label: string }>> = {
  PHASE_STALL: [
    { field: 'stallNotifyDays',  label: 'Stall notify' },
    { field: 'stallUrgentDays',  label: 'Stall urgent' },
    { field: 'stallPartnerDays', label: 'Stall partner' },
  ],
  CLIENT_APPROVAL_OVERDUE: [
    { field: 'approvalNotifyDays',   label: 'Approval notify' },
    { field: 'approvalUrgentDays',   label: 'Approval urgent' },
    { field: 'approvalRenewalDays',  label: 'Link renewal' },
    { field: 'approvalEscalateDays', label: 'Approval escalate' },
  ],
};

const cardCls = 'rounded-xl border shadow-sm transition-shadow duration-150';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};

export default function EscalationOverridesSidebarCard({ projectId }: Props) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Partial<Record<Field, number | null>>>({});
  const [effectiveAllowProceedAtRisk, setEffectiveAllowProceedAtRisk] = useState<boolean | undefined>(undefined);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!open || !projectId) return;
    setLoading(true);
    setError('');
    apiFetch<RulesResponse>(`/projects/${projectId}/escalation-rules`)
      .then(r => { if (!cancelledRef.current) { setRules(r.rules); setEffectiveAllowProceedAtRisk(r.effective?.allowProceedAtRisk); } })
      .catch(e => { if (!cancelledRef.current) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelledRef.current) setLoading(false); });
    return () => { cancelledRef.current = true; };
  }, [open, projectId]);

  const effectiveValue = (ruleType: string, field: Field): number => {
    if (field in overrides && overrides[field] !== null && overrides[field] !== undefined) {
      return overrides[field] as number;
    }
    const rule = rules.find(r => r.ruleType === ruleType);
    if (rule && typeof rule[field] === 'number') return rule[field] as number;
    return PLATFORM_DEFAULTS[field];
  };

  const isOverridden = (ruleType: string, field: Field): boolean => {
    if (field in overrides) return overrides[field] !== null && overrides[field] !== undefined;
    const rule = rules.find(r => r.ruleType === ruleType);
    return !!(rule && rule.source === 'project' && typeof rule[field] === 'number');
  };

  const toggleOverride = (ruleType: string, field: Field) => {
    if (isOverridden(ruleType, field)) {
      setOverrides(prev => ({ ...prev, [field]: null }));
    } else {
      // Seed from the currently inherited effective value so clicking
      // Override and saving without edits doesn't silently flip partner-
      // or tenant-configured thresholds back to the platform default.
      setOverrides(prev => ({ ...prev, [field]: effectiveValue(ruleType, field) }));
    }
    setSavedAt(null);
    setError('');
  };

  const setOverrideValue = (field: Field, raw: string) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    setOverrides(prev => ({ ...prev, [field]: n }));
    setSavedAt(null);
    setError('');
  };

  const handleSave = async () => {
    if (Object.keys(overrides).length === 0) return;
    setSaving(true);
    setError('');

    const stallOrder: Field[] = ['stallNotifyDays', 'stallUrgentDays', 'stallPartnerDays'];
    const approvalOrder: Field[] = ['approvalNotifyDays', 'approvalUrgentDays', 'approvalRenewalDays', 'approvalEscalateDays'];

    const stallTouched = stallOrder.some(f => f in overrides);
    const approvalTouched = approvalOrder.some(f => f in overrides);

    for (const { ruleType, order } of [
      { ruleType: 'PHASE_STALL', order: stallOrder, touched: stallTouched },
      { ruleType: 'CLIENT_APPROVAL_OVERDUE', order: approvalOrder, touched: approvalTouched },
    ].filter(x => x.touched)) {
      const vals = order.map(f => effectiveValue(ruleType, f));
      if (vals.some(v => v < 1 || v > 30)) {
        setError('Days must be between 1 and 30');
        setSaving(false);
        return;
      }
      // Strict monotonic increase across the full chain: 3 values for stall, 4 for approval.
      const monotonic = vals.every((v, i) => i === 0 || vals[i - 1] < v);
      if (!monotonic) {
        setError(`${ruleType === 'PHASE_STALL' ? 'Stall' : 'Approval'} thresholds must be strictly increasing`);
        setSaving(false);
        return;
      }
    }

    try {
      const stallUpdates: Record<string, number | null> = {};
      const approvalUpdates: Record<string, number | null> = {};
      for (const [field, value] of Object.entries(overrides) as Array<[Field, number | null]>) {
        if (stallOrder.includes(field)) stallUpdates[field] = value;
        else if (approvalOrder.includes(field)) approvalUpdates[field] = value;
      }

      if (Object.keys(stallUpdates).length > 0) {
        await apiFetch(`/projects/${projectId}/escalation-rules`, {
          method: 'PATCH',
          body: JSON.stringify({ ruleType: 'PHASE_STALL', ...stallUpdates }),
        });
      }
      if (Object.keys(approvalUpdates).length > 0) {
        await apiFetch(`/projects/${projectId}/escalation-rules`, {
          method: 'PATCH',
          body: JSON.stringify({ ruleType: 'CLIENT_APPROVAL_OVERDUE', ...approvalUpdates }),
        });
      }

      // Refresh
      const refreshed = await apiFetch<RulesResponse>(`/projects/${projectId}/escalation-rules`);
      if (!cancelledRef.current) {
        setRules(refreshed.rules);
        setEffectiveAllowProceedAtRisk(refreshed.effective?.allowProceedAtRisk);
        setOverrides({});
        setSavedAt(Date.now());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // #3188 APPROVE.5 — the project's "Allow proceed at risk" override: true /
  // false sets it, null reverts to the tenant's (or inherited) value. Saved at
  // once, like the tenant-level toggles; the rules are then re-read.
  const approvalRule = rules.find(r => r.ruleType === 'CLIENT_APPROVAL_OVERDUE');
  const showProceedAtRisk = !!approvalRule && 'allowProceedAtRisk' in approvalRule;
  const handleAllowProceedAtRisk = async (value: boolean | null) => {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/escalation-rules`, {
        method: 'PATCH',
        body: JSON.stringify({ ruleType: 'CLIENT_APPROVAL_OVERDUE', allowProceedAtRisk: value }),
      });
      const refreshed = await apiFetch<RulesResponse>(`/projects/${projectId}/escalation-rules`);
      if (!cancelledRef.current) {
        setRules(refreshed.rules);
        setEffectiveAllowProceedAtRisk(refreshed.effective?.allowProceedAtRisk);
        setSavedAt(Date.now());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardCls} style={cardStyle}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="escalation-overrides-toggle"
        aria-expanded={open}
        style={{
          width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <p
          className="uppercase tracking-wider"
          style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            margin: 0, letterSpacing: '0.05em',
            borderLeft: '3px solid var(--primary)', paddingLeft: 8,
          }}
        >
          Escalation Overrides
        </p>
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {loading && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
          )}
          {error && (
            <div data-testid="escalation-overrides-error" style={{
              padding: '6px 10px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
              borderRadius: 6, marginBottom: 10, fontSize: 12,
            }}>{error}</div>
          )}
          {savedAt && !error && (
            <div data-testid="escalation-overrides-saved" style={{
              padding: '6px 10px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
              borderRadius: 6, marginBottom: 10, fontSize: 12,
            }}>Saved</div>
          )}

          {!loading && Object.entries(FIELD_BY_RULE).map(([ruleType, fields]) => (
            <div key={ruleType} style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px 0' }}>
                {ruleType === 'PHASE_STALL' ? 'Phase Stall' : 'Client Approval'}
              </p>
              {fields.map(({ field, label }) => {
                const overridden = isOverridden(ruleType, field);
                const value = effectiveValue(ruleType, field);
                return (
                  <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}>
                      {overridden ? '' : '🔒 '}{label}
                    </span>
                    {overridden ? (
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={value}
                        onChange={e => setOverrideValue(field, e.target.value)}
                        disabled={saving}
                        data-testid={`escalation-override-input-${field}`}
                        style={{
                          width: 50, padding: '2px 6px', fontSize: 12,
                          border: '1px solid var(--border)', borderRadius: 4,
                          background: 'var(--bg)', color: 'var(--text)',
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text)', minWidth: 50, textAlign: 'right' }}>{value}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleOverride(ruleType, field)}
                      disabled={saving}
                      data-testid={`escalation-override-toggle-${field}`}
                      style={{
                        padding: '2px 8px', fontSize: 11, fontWeight: 500,
                        border: `1px solid ${overridden ? 'var(--primary)' : 'var(--border)'}`,
                        background: overridden ? 'rgba(124,58,237,0.08)' : 'transparent',
                        color: overridden ? 'var(--primary)' : 'var(--text-secondary)',
                        borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {overridden ? 'Revert' : 'Override'}
                    </button>
                  </div>
                );
              })}
              {ruleType === 'CLIENT_APPROVAL_OVERDUE' && showProceedAtRisk && approvalRule && (
                <ProceedAtRiskSetting
                  stored={approvalRule.source === 'project' && typeof approvalRule.allowProceedAtRisk === 'boolean' ? approvalRule.allowProceedAtRisk : null}
                  effective={effectiveAllowProceedAtRisk ?? true}
                  disabled={saving}
                  onChange={v => void handleAllowProceedAtRisk(v)}
                />
              )}
            </div>
          ))}

          {Object.keys(overrides).length > 0 && (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="escalation-overrides-save"
              style={{
                width: '100%', padding: '8px 12px', fontSize: 13, fontWeight: 600,
                background: 'var(--primary)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius-sm)', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1, marginTop: 4,
              }}
            >
              {saving ? 'Saving…' : 'Save Overrides'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
